import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { SiteHeader, SiteFooter } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Key, DownloadSimple, ClockCounterClockwise, ShieldCheck, Copy,
  Trash, Plus, CaretLeft, FileText, ChartLineUp,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { safeCopy } from '../lib/safeCopy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUB_STATUS_TONE = {
  pending_approval:  'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]',
  quote_requested:   'bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]',
  quoted:            'bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]',
  active:            'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]',
  expired:           'bg-gray-100 text-[#4B5563] border-gray-300',
  rejected:          'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]',
  cancelled:         'bg-gray-100 text-[#4B5563] border-gray-300',
};
const PURCHASE_STATUS_TONE = {
  pending_review:    'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]',
  auto_generating:   'bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]',
  delivered:         'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]',
  rejected:          'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]',
  generation_failed: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]',
};

function StatusChip({ status, map }) {
  const tone = map[status] || 'bg-gray-100 text-[#4B5563] border-gray-300';
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${tone}`} data-testid={`status-${status}`}>{status?.replace(/_/g, ' ')}</span>;
}

function NewTokenDialog({ open, onOpenChange, onIssued }) {
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [issued, setIssued] = useState(null);

  useEffect(() => { if (open) { setLabel(''); setErr(''); setIssued(null); } }, [open]);

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/marketplace/api-tokens/request`, { label }, { withCredentials: true });
      setIssued(r.data);
      onIssued();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="token-issue-dialog">
        <DialogHeader>
          <DialogTitle>Issue API token</DialogTitle>
        </DialogHeader>
        {!issued ? (
          <div className="space-y-3">
            <p className="text-sm text-[#4B5563]">
              Tokens are bound to your active Enterprise API subscription. Rate-limit defaults to <strong>60 req/min</strong>; your subscription monthly quota also applies.
            </p>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Token label (optional)</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. ERP integration · staging" className="mt-1 h-9 text-sm" data-testid="token-label" />
            </div>
            {err && <p className="text-[12px] text-[#9B1C1C]" data-testid="token-issue-error">{err}</p>}
            <Button onClick={submit} disabled={saving} className="w-full h-10 rounded bg-[#FF4E6A] hover:bg-[#E04057] text-white font-semibold" data-testid="token-issue-submit">
              {saving ? 'Issuing…' : 'Issue token'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-3 rounded bg-[#FEF3C7] border-l-4 border-[#F3CC64]">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#723B13]">Store this token now</p>
              <p className="text-[12px] text-[#723B13]">It will not be shown again. Copy it into your password manager or environment file before closing this dialog.</p>
            </div>
            <div className="bg-[#0B1426] text-[#FF4E6A] font-mono text-[11px] p-3 rounded break-all" data-testid="token-plaintext">
              {issued.token_plaintext}
            </div>
            <div className="flex gap-2">
              <Button onClick={async () => { const ok = await safeCopy(issued.token_plaintext); if (ok) toast.success('Token copied'); else toast.info('Copy blocked — select the token above to copy manually'); }}
                className="flex-1 h-9 rounded bg-[#0B1426] text-white text-xs font-semibold" data-testid="token-copy">
                <Copy size={13} weight="bold" className="mr-1.5" /> Copy
              </Button>
              <Button onClick={() => onOpenChange(false)} variant="outline" className="flex-1 h-9 rounded text-xs">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MarketplaceMyLicensesPage() {
  const [subs, setSubs] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [tab, setTab] = useState('subscriptions');
  const [newTokenOpen, setNewTokenOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, t] = await Promise.all([
        axios.get(`${API_URL}/api/marketplace/subscriptions/mine`, { withCredentials: true }),
        axios.get(`${API_URL}/api/marketplace/purchases/mine`, { withCredentials: true }),
        axios.get(`${API_URL}/api/marketplace/api-tokens/mine`, { withCredentials: true }),
      ]);
      setSubs(s.data.items || []);
      setPurchases(p.data.items || []);
      setTokens(t.data.items || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stripe return — poll status until final (max 6 tries, 1.5s apart)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (!sid) return;
    let attempts = 0;
    const id = setInterval(async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API_URL}/api/marketplace/stripe/checkout/status/${sid}`,
                                  { withCredentials: true });
        if (r.data?.status === 'final') {
          clearInterval(id);
          toast.success(`Payment ${r.data?.payment_status} — license updated.`);
          searchParams.delete('session_id');
          setSearchParams(searchParams, { replace: true });
          load();
        } else if (attempts >= 6) {
          clearInterval(id);
          toast.info('Payment still processing. Refresh in a moment.');
        }
      } catch { if (attempts >= 6) clearInterval(id); }
    }, 1500);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const download = (id, fname) => {
    const a = document.createElement('a');
    a.href = `${API_URL}/api/marketplace/purchases/${id}/download`;
    a.target = '_blank';
    a.download = fname || 'report.pdf';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this token? Existing integrations will start failing immediately.')) return;
    try {
      await axios.delete(`${API_URL}/api/marketplace/api-tokens/${id}`, { withCredentials: true });
      toast.success('Token revoked');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <div className="mb-6">
          <Link to="/marketplace" className="text-[12px] text-[#4B5563] hover:text-[#111827] flex items-center gap-1 mb-2" data-testid="mp-back-to-catalog">
            <CaretLeft size={12} /> Back to Marketplace
          </Link>
          <div className="flex items-center gap-2">
            <ShieldCheck size={22} className="text-[#FF4E6A]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">My Licenses</h1>
          </div>
          <p className="text-sm text-[#4B5563] mt-1">Manage your subscriptions, individual reports, and API tokens.</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-5 border-b border-gray-200">
          {[
            { k: 'subscriptions', label: 'Subscriptions', count: subs.length },
            { k: 'purchases', label: 'Reports', count: purchases.length },
            { k: 'tokens', label: 'API tokens', count: tokens.length },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 ${tab === t.k ? 'border-[#FF4E6A] text-[#111827]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'}`}
              data-testid={`my-tab-${t.k}`}>
              {t.label} <span className="text-[10px] text-[#9CA3AF] ml-1">{t.count}</span>
            </button>
          ))}
        </div>

        {loading ? <p className="text-sm text-[#4B5563] py-10 text-center">Loading…</p> : (
          <>
            {tab === 'subscriptions' && (
              <div className="space-y-3" data-testid="my-subs-list">
                {subs.length === 0 ? (
                  <div className="card-flat p-8 bg-white text-center text-sm text-[#4B5563]">
                    No subscriptions yet. <Link to="/marketplace" className="text-[#FF4E6A] font-semibold">Browse the catalog →</Link>
                  </div>
                ) : subs.map(s => (
                  <div key={s.id} className="card-flat p-4 bg-white" data-testid={`sub-row-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{s.ref} · {s.billing_period}</p>
                        <h3 className="text-base font-bold text-[#111827] mt-0.5">{s.plan_name}</h3>
                        <p className="text-[12px] text-[#4B5563] mt-1">
                          {s.price_usd ? `$${s.price_usd.toLocaleString()} ` : ''}
                          {s.quota_lookups_per_month ? `· quota ${s.quota_lookups_per_month.toLocaleString()} lookups/mo` : ''}
                          {s.expires_at ? ` · expires ${new Date(s.expires_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <StatusChip status={s.status} map={SUB_STATUS_TONE} />
                    </div>
                    {s.lookups_used_this_period > 0 && s.quota_lookups_per_month > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-[10px] text-[#4B5563] mb-0.5">
                          <span>{s.lookups_used_this_period.toLocaleString()} used</span>
                          <span>{Math.round(s.lookups_used_this_period * 100 / s.quota_lookups_per_month)}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#FF4E6A]" style={{ width: `${Math.min(100, s.lookups_used_this_period * 100 / s.quota_lookups_per_month)}%` }} />
                        </div>
                      </div>
                    )}
                    {s.rejection_reason && <p className="text-[11px] text-[#9B1C1C] mt-2">Rejection: {s.rejection_reason}</p>}
                  </div>
                ))}
              </div>
            )}

            {tab === 'purchases' && (
              <div className="space-y-3" data-testid="my-purchases-list">
                {purchases.length === 0 ? (
                  <div className="card-flat p-8 bg-white text-center text-sm text-[#4B5563]">
                    No purchases yet. <Link to="/marketplace" className="text-[#FF4E6A] font-semibold">Browse reports →</Link>
                  </div>
                ) : purchases.map(p => (
                  <div key={p.id} className="card-flat p-4 bg-white" data-testid={`purchase-row-${p.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{p.ref} · {p.item_type}</p>
                        <h3 className="text-base font-bold text-[#111827] mt-0.5">{p.item_label}</h3>
                        <p className="text-[12px] text-[#4B5563] mt-1">{p.tier_name} · ${p.price_usd}</p>
                      </div>
                      <StatusChip status={p.status} map={PURCHASE_STATUS_TONE} />
                    </div>
                    {p.status === 'delivered' && (
                      <Button onClick={() => download(p.id, p.pdf_filename)} className="mt-3 h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid={`purchase-download-${p.id}`}>
                        <DownloadSimple size={12} weight="bold" className="mr-1" /> Download PDF
                      </Button>
                    )}
                    {p.admin_review_required && p.status === 'pending_review' && (
                      <p className="text-[11px] text-[#723B13] mt-2 flex items-center gap-1"><ClockCounterClockwise size={11} /> Queued for Aᴳ assessor review — typically delivered within 2 business days.</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === 'tokens' && (
              <div data-testid="my-tokens-list">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-[12px] text-[#4B5563]">
                    Tokens are bound to your active Enterprise API subscription. Treat them like passwords; rotate often.
                  </p>
                  <Button onClick={() => setNewTokenOpen(true)} className="h-9 rounded bg-[#FF4E6A] hover:bg-[#E04057] text-white text-sm font-semibold" data-testid="token-new">
                    <Plus size={13} weight="bold" className="mr-1" /> Issue token
                  </Button>
                </div>
                {tokens.length === 0 ? (
                  <div className="card-flat p-8 bg-white text-center text-sm text-[#4B5563]">
                    No tokens issued. You need an active Enterprise API subscription first.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tokens.map(t => (
                      <div key={t.id} className="card-flat p-4 bg-white flex items-center justify-between gap-3" data-testid={`token-row-${t.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-[#111827]">{t.label || 'Token'}</p>
                          <p className="text-[11px] text-[#4B5563]">
                            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{t.token_prefix}…</span>
                            {' · '}{t.plan_code}{t.revoked_at ? ' · revoked' : ''}
                          </p>
                          <p className="text-[10px] text-[#6B7280] tabular-nums">
                            <Key size={9} weight="bold" className="inline mr-0.5" />
                            {t.total_calls?.toLocaleString() || 0} total calls · last used {t.last_used_at ? new Date(t.last_used_at).toLocaleDateString() : 'never'}
                          </p>
                        </div>
                        {!t.revoked_at && (
                          <Button onClick={() => revoke(t.id)} variant="outline" className="h-8 text-xs rounded text-[#9B1C1C] border-[#F8B4B4] hover:bg-[#FDE8E8]" data-testid={`token-revoke-${t.id}`}>
                            <Trash size={12} className="mr-1" /> Revoke
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Usage hint */}
        <div className="mt-8 p-5 rounded-lg bg-[#0B1426] text-white">
          <div className="flex items-start gap-3">
            <ChartLineUp size={18} weight="duotone" className="text-[#FF4E6A]" />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/60 mb-1">API quickstart</p>
              <p className="text-sm text-white/80 mb-2">Authenticate with a Bearer token and hit:</p>
              <code className="block bg-black/30 p-2 rounded text-[11px] font-mono text-[#FF4E6A]">
                GET {API_URL}/api/marketplace/v1/ingredient/&lt;INCI&gt;<br/>
                Authorization: Bearer ti_live_…
              </code>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <NewTokenDialog open={newTokenOpen} onOpenChange={setNewTokenOpen} onIssued={load} />
    </div>
  );
}
